import React, { Suspense } from 'react'
import c from 'classnames'
import { connect } from 'react-redux'
import { getActiveQuery, getActiveParams } from 'selectors/pageStateSelectors'
import { toggleQueryValue, removeFromPageState } from 'actions/pageStateActions'

import Modal, { ModalBody, ModalControls, ModalClose } from 'discada/dist/Modal'
import Loading from 'discada/dist/Loading'
import Button from 'discada/dist/Button'
import { Controls } from 'discada/dist/Form'

import VRP from 'components/VRP'
import Rebates from 'components/Quote/Rebates'
import RebateDetails from 'components/Quote/RebateDetails'
import Trades from 'components/Quote/Trades'
import GenericAdditions from 'components/Quote/Additions'
import QV from 'components/Quote/QuoteValue'
import ProgramDetails from 'components/Quote/ProgramDetails'
import { INTENTS } from 'reducers/quoteReducers'

import StateFeeTaxAutoFormModal from 'components/widgets/StateFeeTaxAutoFormModal'
import { getIsLoggedIn } from 'selectors/authSelectors'
import {
  getProfileOk,
  getRooftopName,
  getRooftopInfo,
} from 'selectors/appStatusSelectors'

import AddVehicleForm from 'components/forms/AddVehicleForm'
import EditUserForm from 'components/forms/EditUserForm'
import SalesLeadForm from 'components/forms/SalesLeadForm'
import Warranty from 'components/VRP/Warranty'
import ProgramInfo from 'components/Quote/ProgramInfo'
import Slider from 'components/Quote/SimpleQuote/Slider'

import AddConfigForm from 'components/forms/AddConfigForm'
import { InventoryFilters } from 'pages/inventory/InventoryFilters'
import { getVehiclesForVLP } from 'actions/inventoryActions'
import { tagCustomer, deleteConfig } from 'actions/appStatusActions'

import QuoteSaveForm from 'components/forms/QuoteSaveForm'

import VehicleSettings from 'components/Settings/Vehicle'
import BankSettings from 'components/Settings/Bank'
import Integrations from 'components/Settings/Integrations'

import ChooseYourOwnAdventure from 'components/ChooseYourOwnAdventure'
import { AppFrame } from 'pages/settings/mportal'

import SqueezeModal from 'components/widgets/Reactions/SqueezeModal'
import { makeStaticAssetPath } from 'utils'

import { DistractorModal } from 'components/widgets/Distractor'

import { refreshPrograms } from 'actions/programActions'
import { getQuoteDirty } from 'selectors/quoteSelectors'

import './Modals.scss'

const MagicLinker = React.lazy(() => import('components/widgets/MagicLinker'))
const ServiceIQSimulator = React.lazy(() =>
  import('components/widgets/ServiceIQSimulator'),
)
const PaymentIQ = React.lazy(() => import('components/widgets/PaymentIQ'))
const ShowAndTell = React.lazy(() => import('components/widgets/ShowAndTell'))
const LeadDetails = React.lazy(() => import('components/Lead'))

const Scripts = React.lazy(() => import('components/Settings/Scripts'))

export const ModalSubmitSet = ({ onSave, onCancel, children, saveLabel }) => {
  return (
    <ModalControls>
      {onSave && (
        <Button size="small" success onClick={onSave}>
          {saveLabel || 'Apply to Your Quote'}
        </Button>
      )}
      {onCancel && (
        <Button size="small" text onClick={onCancel}>
          Close
        </Button>
      )}
      {children}
    </ModalControls>
  )
}

export function Modals({
  query,
  params,
  removeFromPageState,
  getVehiclesForVLP,
  deleteConfig,
  squeezeJacketOk,
  tagCustomer,
  rooftopName,
  rooftopInfo,
  refreshPrograms,
  isQuoteDirty,
}) {
  let r = params => removeFromPageState(params, true)

  const closeFiltersModal = () => {
      getVehiclesForVLP()
      r(['showInventoryFilters', 'filterGroup'])
    },
    closeUserModal = () => r(['addUser', 'editUserId'])

  let onVRPClose = () =>
      r(['vehicleId', 'quoteId', 'dealtuner.section', 'customerView']),
    closeQuoteModal = refresh => {
      r([
        'changeZip',
        'addRebate',
        'addTrade',
        'showFandI',
        'contractDetails',
        'printModal',
        'autoPrint',
      ])

      if (refresh || isQuoteDirty) refreshPrograms({ showDistractor: true })
    }

  return (
    <div className="modals-lot">
      {!!query.vehicleId && (
        <React.Fragment>
          <Modal onClose={onVRPClose} className="vrp-modal vrp-fixed-header">
            <VRP
              noswitch
              showHeader
              id={query.vehicleId}
              quoteId={query.quoteId}
            />
          </Modal>
        </React.Fragment>
      )}

      {query.showTaxConfig && (
        <Modal
          onClose={() => r(['showTaxConfig'])}
          className="auto-width state-fee-tax-modal"
        >
          <ModalBody>
            <StateFeeTaxAutoFormModal
              size="small"
              onClose={() => r(['showTaxConfig'])}
              quoteId={query.quoteId}
              vehicleId={params.id}
            />
          </ModalBody>
        </Modal>
      )}

      {query.showRooftopModal && (
        <Modal
          onClose={() => r(['showRooftopModal'])}
          className="auto-width rooftop-select-modal"
        >
          <ModalBody>
            <div className="container">
              <div className="columns">
                <div className="column">
                  <h1 className="is-size-3">Where would you like to go?</h1>
                  <ChooseYourOwnAdventure />
                </div>
              </div>
            </div>
          </ModalBody>
        </Modal>
      )}

      {query.leadId && (
        <Modal
          className={c('lead-modal no-padding')}
          onClose={() => r(['leadId'])}
        >
          <ModalBody>
            <Suspense fallback={<Loading />}>
              <LeadDetails id={query.leadId} />
            </Suspense>
          </ModalBody>
        </Modal>
      )}

      {(query.addConfig || query.cloneConfig) && (
        <Modal
          onClose={() => r(['addConfig', 'cloneConfig'])}
          className={c('auto-width inventory-filter-modal', {
            'clone-config-modal': query.cloneConfig,
          })}
        >
          <ModalBody>
            <AddConfigForm
              onCancel={() => r(['addConfig', 'cloneConfig'])}
              category={query.addConfig}
              cloneId={query.cloneConfig}
            />
          </ModalBody>
        </Modal>
      )}

      {query.deleteConfig && (
        <Modal onClose={() => r(['deleteConfig'])} className="top-level">
          <ModalBody>
            https://corfield.org/{' '}
            <article className={c('message', 'is-danger')}>
              <div className="message-body">
                <h4>
                  Any cars that are using these settings to generate pricing
                  will fallback to the next best matching settings.
                  <br />
                  Deleting this config can't be undone.
                </h4>
              </div>
            </article>
            <Controls
              saveLabel="Delete Settings"
              onSave={() => deleteConfig(query.deleteConfig)}
              onCancel={() => r(['deleteConfig'])}
              danger
            />
          </ModalBody>
        </Modal>
      )}

      {query.addVehicle && (
        <Modal onClose={() => r(['addVehicle'])}>
          <ModalBody>
            <AddVehicleForm onCancel={() => r(['addVehicle'])} />
          </ModalBody>
        </Modal>
      )}

      {query.addRebate && (
        <Modal
          onClose={closeQuoteModal}
          noClose
          className="inside-close rebates-modal"
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={closeQuoteModal}
            />
            <Rebates {...query} vehicleId={params.id} hideTitle />
          </ModalBody>
          <ModalSubmitSet onSave={closeQuoteModal} onCancel={closeQuoteModal} />
        </Modal>
      )}

      {query.rebateDetails && (
        <Modal
          noClose
          onClose={() => r(['rebateDetails'])}
          className="inside-close rebates-details-modal quote-action-modal"
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['rebateDetails'])}
            />
            <RebateDetails
              {...query}
              vehicleId={params.id}
              rebateId={query.rebateDetails}
            />
          </ModalBody>
          <ModalSubmitSet onCancel={() => r(['rebateDetails'])} />
        </Modal>
      )}

      {query.addTrade && (
        <Modal onClose={closeQuoteModal} noClose className="inside-close">
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={closeQuoteModal}
            />
            <br />
            <br />
            <Trades {...query} vehicleId={params.id} />
          </ModalBody>
          <ModalSubmitSet onSave={closeQuoteModal} onCancel={closeQuoteModal} />
        </Modal>
      )}

      {query.contractDetails && (
        <Modal onClose={closeQuoteModal}>
          <ModalBody>
            <ProgramInfo
              {...query}
              key="modal-info"
              vehicleId={params.id}
              mode={query.contractDetails}
            />
          </ModalBody>
          <ModalSubmitSet onCancel={closeQuoteModal}>
            <Button text onClick={() => window.print()}>
              Print&hellip;
            </Button>
          </ModalSubmitSet>
        </Modal>
      )}

      {query.showFandI && (
        <Modal noClose className="inside-close quote-fandi">
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['showFandI'])}
            />
            <Warranty
              id={params.id}
              quoteId={query.quoteId}
              mode={query.showFandI}
              header="Included Coverage"
            />

            <h1>Select Addons and Extras</h1>
            <GenericAdditions
              {...query}
              title="Additions"
              addsKey="additions"
            />
            <GenericAdditions
              {...query}
              title="Warranties"
              addsKey="warranties"
            />
            <GenericAdditions
              {...query}
              title="Maintenance Plans"
              addsKey="maintenancePlans"
            />
          </ModalBody>
          <ModalSubmitSet onSave={closeQuoteModal} onCancel={closeQuoteModal} />
        </Modal>
      )}

      {query.quoteAction && query.mode && !query.ioi && (
        <Modal noClose className="inside-close quote-action-modal">
          <ModalBody>
            {query.quoteAction !== INTENTS.contact && (
              <ModalClose
                classaName="filter-close-button"
                onClose={() => r(['quoteAction', 'mode', 'quoteSaved'])}
              />
            )}
            <QuoteSaveForm
              vehicleId={params.id}
              {...query}
              intent={query.quoteAction}
              onCancel={() => r(['quoteAction', 'mode', 'quoteSaved'])}
              autoSelect
            />
          </ModalBody>
        </Modal>
      )}

      {query.changeZip && (
        <Modal className="auto-width zip-code-modal" onClose={closeQuoteModal}>
          <ModalBody>
            <h1>Enter your Zip Code</h1>
            {query.changeZip === 'error' && (
              <article className="message is-danger">
                <div className="message-body">
                  We had a problem verifying this zip code. Please enter a valid
                  5-digit zip code.
                </div>
              </article>
            )}
            <QV
              id={query.quoteId}
              vehicleId={query.vehicleId || params.id}
              valueKey="customerZipCode"
              permit="customize"
              onBeforeChange={v =>
                tagCustomer(null, null, true, {
                  zipCode: v,
                })
              }
            />
          </ModalBody>
          <ModalSubmitSet onSave={closeQuoteModal} />
        </Modal>
      )}

      {query.showInventoryFilters && (
        <Modal
          noClose
          onClose={closeFiltersModal}
          className={c(
            'auto-width',
            'auto-height',
            'no-padding',
            'inventory-filter-modal',
            {
              'power-search': !query.filterGroup,
            },
          )}
        >
          <ModalBody>
            <InventoryFilters
              group={query.filterGroup}
              closeFiltersModal={closeFiltersModal}
            />
          </ModalBody>
          <ModalSubmitSet>
            <Button
              size="small"
              primary
              icon="search"
              onClick={closeFiltersModal}
            >
              See Results
            </Button>
            <div>
              <Button size="small" text onClick={closeFiltersModal}>
                Close
              </Button>
              {query.filterGroup && (
                <Button
                  size="small"
                  text
                  onClick={() => {
                    r(`inventory.${query.filterGroup}`)
                  }}
                >
                  Clear Selected
                </Button>
              )}
            </div>
          </ModalSubmitSet>
        </Modal>
      )}

      {(query.addUser || query.editUserId) && (
        <Modal
          noClose
          className={c('auto-width edit-user-modal')}
          onClose={closeUserModal}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={closeUserModal}
            />
            <EditUserForm id={query.editUserId} onCancel={closeUserModal} />
          </ModalBody>
        </Modal>
      )}

      {false && query.customizeDemo && (
        <Modal
          className={c('auto-width edit-user-modal')}
          onClose={() => r(['customizeDemo', 'quoteAction'])}
          noClose={!query.customizeDemo}
        >
          <ModalBody>
            <SalesLeadForm />
          </ModalBody>
        </Modal>
      )}

      {query.paymentFinder && (
        <Modal
          noClose
          className={c('auto-width payment-finder-modal inside-close')}
          onClose={() => r(['paymentFinder'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['paymentFinder'])}
            />
            <Slider
              vehicleId={query.vehicleId || params.id}
              quoteId={query.quoteId}
              {...query}
            />
          </ModalBody>
        </Modal>
      )}

      {query.disclaimer && (
        <Modal
          noClose
          className={c('auto-width disclaimer-modal inside-close')}
          onClose={() => r(['disclaimer'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['disclaimer'])}
            />
            <ProgramDetails
              vehicleId={query.vehicleId || params.id}
              {...query}
            />
          </ModalBody>
        </Modal>
      )}

      {query.vehicleConfig && (
        <Modal
          noClose
          className={c('auto-width config-modal')}
          onClose={() => r(['vehicleConfig'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['vehicleConfig'])}
            />
            <VehicleSettings
              id={query.vehicleConfig}
              onClose={() => r(['vehicleConfig'])}
            />
          </ModalBody>
        </Modal>
      )}

      {query.bankConfig && (
        <Modal
          noClose
          className={c('auto-width config-modal')}
          onClose={() => r(['bankConfig'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['bankConfig'])}
            />
            <BankSettings
              id={query.bankConfig}
              onClose={() => r(['bankConfig'])}
            />
          </ModalBody>
        </Modal>
      )}

      {query.rooftopConfig && (
        <Modal
          noClose
          className={c('auto-width config-modal')}
          onClose={() => r(['rooftopConfig'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['rooftopConfig'])}
            />
          </ModalBody>
        </Modal>
      )}

      {query.magicLinker && (
        <Modal
          noClose
          className={c('auto-width config-modal')}
          onClose={() => r(['magicLinker'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['magicLinker'])}
            />
            <Suspense fallback={<Loading />}>
              <MagicLinker onCancel={() => r(['magicLinker'])} />
            </Suspense>
          </ModalBody>
        </Modal>
      )}

      {query.serviceiqSimulator && (
        <Modal
          noClose
          className={c('auto-width config-modal')}
          onClose={() => r(['serviceiqSimulator'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['serviceiqSimulator'])}
            />
            <Suspense fallback={<Loading />}>
              <ServiceIQSimulator onCancel={() => r(['serviceiqSimulator'])} />
            </Suspense>
          </ModalBody>
        </Modal>
      )}

      {query.scripts && (
        <Modal
          noClose
          className={c('auto-width config-modal scripts-modal')}
          onClose={() => r(['scripts'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['scripts'])}
            />
            <Suspense fallback={<Loading />}>
              <Scripts onCancel={() => r(['scripts'])}>
                {doSave => (
                  <ModalSubmitSet
                    saveLabel="Save Scripts"
                    onSave={doSave}
                    onCancel={() => r(['scripts'])}
                  />
                )}
              </Scripts>
            </Suspense>
          </ModalBody>
        </Modal>
      )}

      {query.showAndTell && (
        <Modal
          noClose
          className={c('auto-width config-modal')}
          onClose={() => r(['showAndTell'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['showAndTell'])}
            />
            <Suspense fallback={<Loading />}>
              <ShowAndTell onCancel={() => r(['showAndTell'])} />
            </Suspense>
          </ModalBody>
        </Modal>
      )}

      {query.custConfigAppointmentsReportEmailRecipients && (
        <Modal
          noClose
          className={c('config-modal')}
          onClose={() => r(['custConfigAppointmentsReportEmailRecipients'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['custConfigAppointmentsReportEmailRecipients'])}
            />
            <Suspense fallback={<Loading />}>
              <AppFrame
                messageApi
                scrolling
                resize
                frameId="appointment-email-report-recipients"
                title="Email Report Recipients"
                src={`/view/iq-cust-config?rooftop-id=${rooftopInfo.uuid}`}
              />
            </Suspense>
          </ModalBody>
        </Modal>
      )}

      {query.paymentIQ && (
        <Modal
          noClose
          className={c('config-modal paymentIQ-modal')}
          onClose={() => r(['paymentIQ'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['paymentIQ'])}
            />
            <Suspense fallback={<Loading />}>
              <PaymentIQ onCancel={() => r(['paymentIQ'])} />
            </Suspense>
          </ModalBody>
        </Modal>
      )}

      {query.integrationSettings && (
        <Modal
          noClose
          className={c('auto-width config-modal')}
          onClose={() => r(['integrationSettings'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['integrationSettings'])}
            />
            <Suspense fallback={<Loading />}>
              <Integrations />
            </Suspense>
          </ModalBody>
        </Modal>
      )}

      {query.sessionId && (
        <Modal
          noClose
          className={c('auto-width lead-details-modal')}
          onClose={() => r(['sessionId'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['sessionId'])}
            />
            <Suspense fallback={<Loading />}>
              <LeadDetails id={query.sessionId} />
            </Suspense>
          </ModalBody>
        </Modal>
      )}

      {query.ioi && (
        <Modal
          onClose={() => r(['ioi'])}
          className={c(
            'auto-width lead-details-modal full-height ioi-modal no-overflow',
          )}
        >
          <ModalControls>
            <Button text small onClick={() => r(['ioi'])} className="ioi-close">
              Return to Your Quote at <strong>{rooftopName}</strong>
            </Button>
          </ModalControls>
          <iframe title="Integrated Insurance Offer" src={atob(query.ioi)} />
        </Modal>
      )}

      {query.quoteSetting && ( // TODO this is the modal deep link mportal setting
        <Modal
          noClose
          className={c('mportal-modal')}
          onClose={() => r(['quoteSetting'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button "
              onClose={() => r(['quoteSetting'])}
            />
            <AppFrame
              frameId="mportal-frame"
              title="mportal-settings"
              src={makeStaticAssetPath(
                `mportal/index.html#/${query.quoteSetting}`,
              )}
              messageApi
            />
          </ModalBody>
        </Modal>
      )}

      {squeezeJacketOk && <SqueezeModal />}
      {query.distractor && <DistractorModal />}

      {query.equityIqSettings && (
        <Modal
          noClose
          className={c('htmx-container', 'equity-iq-settings-modal')}
          onClose={() => r(['equityIqSettings'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['equityIqSettings'])}
            />
            <AppFrame
              frameId="htmx-container"
              title="eiq-edit-frame"
              src={`/view/cust-config?rooftop-id=${query.equityIqSettings}`}
              style={{ height: '99vh' }}
              messageApi
            />
          </ModalBody>
        </Modal>
      )}

      {query.smsNumberSettings && (
        <Modal
          noClose
          className={c('htmx-container', 'equity-iq-settings-modal')}
          onClose={() => r(['smsNumberSettings'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['smsNumberSettings'])}
            />
            <AppFrame
              frameId="htmx-container"
              title="eiq-edit-frame"
              src={`/view/sms-number-config?rooftop-id=${query.smsNumberSettings}`}
              style={{ height: '99vh' }}
              messageApi
            />
          </ModalBody>
        </Modal>
      )}

      {query.mailLogoSettings && (
        <Modal
          noClose
          className={c('htmx-container', 'equity-iq-settings-modal')}
          onClose={() => r(['mailLogoSettings'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['mailLogoSettings'])}
            />
            <AppFrame
              frameId="htmx-container"
              title="eiq-edit-frame"
              src={`/view/rooftop-logo?rooftop-id=${query.mailLogoSettings}`}
              style={{ height: '99vh' }}
              messageApi
              resize={false}
            />
          </ModalBody>
        </Modal>
      )}
    </div>
  )
}

function mapStateToProps(state, op) {
  return {
    ...op,
    query: getActiveQuery(state),
    params: getActiveParams(state),
    isLoggedIn: getIsLoggedIn(state),
    squeezeJacketOk: getProfileOk(state),
    rooftopName: getRooftopName(state),
    rooftopInfo: getRooftopInfo(state),
    isQuoteDirty: getQuoteDirty(state),
  }
}

export default connect(
  mapStateToProps,
  {
    toggleQueryValue,
    removeFromPageState,
    getVehiclesForVLP,
    deleteConfig,
    tagCustomer,
    refreshPrograms,
    getRooftopInfo,
  },
)(Modals)
